import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';
import { useId, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import Btn, { type ButtonAriaProps } from '../actions/Btn';
import { IconAngleDown, IconAngleUp, IconDragHandle } from '../icons';
import Card from './Card';

export interface CollapsibleCardProps {
    title: ReactNode;
    children?: ReactNode;
    meta?: ReactNode;
    actions?: ReactNode;
    /** When true, shows a dnd-kit drag handle. Requires `id`. */
    draggable?: boolean;
    /** Sortable id — required when `draggable` is true. */
    id?: string;
    /**
     * External dnd-kit handle props (e.g. from a parent `SortableItem`).
     * When set, shows the drag handle without owning `useSortable`.
     */
    dragHandleProps?: Record<string, unknown>;
    defaultExpanded?: boolean;
    expanded?: boolean;
    onExpandedChange?: (expanded: boolean) => void;
    /** Header style. Default is navy; `light` uses a light gray header. */
    variant?: 'default' | 'light';
    /** Card and header corner radius. `square` is for nested rows inside a rounded parent. */
    corners?: 'rounded' | 'square';
    className?: string;
    cardClasses?: string;
    /** Applied to the card root (e.g. CSS variables for custom header colors). */
    style?: CSSProperties;
    /** DOM id on the card root, for scrolling back to this section. */
    htmlId?: string;
}

type CollapsibleCardInnerProps = CollapsibleCardProps & {
    setNodeRef?: (node: HTMLElement | null) => void;
    /** Merged with `style` from props (e.g. sortable transform). */
    sortableStyle?: CSSProperties;
};

const CollapsibleCardInner = ({
    title,
    children,
    meta,
    actions,
    defaultExpanded = true,
    expanded: expandedProp,
    onExpandedChange,
    className,
    cardClasses,
    variant = 'default',
    corners = 'rounded',
    dragHandleProps,
    setNodeRef,
    style,
    sortableStyle,
    htmlId,
}: CollapsibleCardInnerProps) => {
    const reactId = useId();
    const containerId = `collapsible-card-container-${reactId}`;
    const [uncontrolledExpanded, setUncontrolledExpanded] = useState(defaultExpanded);
    const isControlled = expandedProp !== undefined;
    const expanded = isControlled ? expandedProp : uncontrolledExpanded;

    const setExpanded = (next: boolean) => {
        if (!isControlled) {
            setUncontrolledExpanded(next);
        }
        onExpandedChange?.(next);
    };

    const toggleExpanded = () => setExpanded(!expanded);

    const handleHeaderClick = (event: MouseEvent<HTMLDivElement>) => {
        const target = event.target as HTMLElement | null;
        if (!target) return;
        // Keep actions, drag handle, and nested form controls from toggling the section.
        if (
            target.closest(
                '.collapsible-card-actions, .collapsible-card-drag-handle, .collapsible-card-toggle, button, input, textarea, select, a, [contenteditable="true"], .search-suggest, .ds-section-catalog-picker, .ds-section-heading-wrap',
            )
        ) {
            return;
        }
        toggleExpanded();
    };

    return (
        <Card
            ref={setNodeRef}
            style={{ ...style, ...sortableStyle }}
            className={clsx(
                'collapsible-card',
                variant === 'light' && 'collapsible-card-light',
                corners === 'square' && 'collapsible-card-square',
                className,
            )}
            cardClasses={cardClasses}
            id={htmlId}
            data-expanded={expanded ? 'true' : 'false'}
        >
            <div className="collapsible-card-header" onClick={handleHeaderClick}>
                <div className="collapsible-card-header-start">
                    {dragHandleProps ? (
                        <Btn
                            variant="ghost"
                            buttonSize="sm"
                            buttonClasses="collapsible-card-drag-handle"
                            buttonIcon={<IconDragHandle />}
                            ariaLabel={
                                typeof title === 'string' ? `Drag to reorder ${title}` : 'Drag to reorder'
                            }
                            buttonAriaProps={dragHandleProps as ButtonAriaProps}
                            buttonOnClick={(event) => event.stopPropagation()}
                        />
                    ) : null}
                    <Btn
                        variant="ghost"
                        buttonSize="sm"
                        buttonClasses="collapsible-card-toggle"
                        buttonIcon={
                            expanded ? (
                                <IconAngleUp iconClasses="collapsible-card-toggle-icon" />
                            ) : (
                                <IconAngleDown iconClasses="collapsible-card-toggle-icon" />
                            )
                        }
                        ariaLabel={expanded ? 'Collapse section' : 'Expand section'}
                        buttonOnClick={(event) => {
                            event.stopPropagation();
                            toggleExpanded();
                        }}
                        buttonAriaProps={{
                            'aria-expanded': expanded,
                            'aria-controls': containerId,
                        }}
                    />
                </div>
                <div className="collapsible-card-header-title">{title}</div>
                <div className="collapsible-card-header-end">
                    {meta ? <div className="collapsible-card-meta">{meta}</div> : null}
                    {actions ? (
                        <div
                            className="collapsible-card-actions"
                            onClick={(event) => event.stopPropagation()}
                        >
                            {actions}
                        </div>
                    ) : null}
                </div>
            </div>
            {expanded ? (
                <div className="collapsible-card-container" id={containerId}>
                    {children}
                </div>
            ) : null}
        </Card>
    );
};

const CollapsibleCardSortable = (props: CollapsibleCardProps & { id: string }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: props.id,
    });
    const style: CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <CollapsibleCardInner
            {...props}
            setNodeRef={setNodeRef}
            sortableStyle={style}
            dragHandleProps={{ ...attributes, ...listeners }}
        />
    );
};

const CollapsibleCard = (props: CollapsibleCardProps) => {
    if (props.draggable) {
        if (!props.id) {
            if (process.env.NODE_ENV !== 'production') {
                console.warn('CollapsibleCard: `id` is required when `draggable` is true.');
            }
            return <CollapsibleCardInner {...props} />;
        }
        return <CollapsibleCardSortable {...props} id={props.id} />;
    }

    return <CollapsibleCardInner {...props} />;
};

export default CollapsibleCard;
